import { Theme, styled, } from '@mui/material/styles';
import RadioGroup, { useRadioGroup } from '@mui/material/RadioGroup';
import FormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { Box, FormControl, FormHelperText, FormLabel, Grid, Typography, useTheme } from '@mui/material';
import { useField } from 'formik';
import { ChangeEvent } from 'react';
import { SxProps } from '@mui/system';
import { useFieldRequired } from './FormSchemaContext';

interface StyledFormControlLabelProps extends FormControlLabelProps {
  checked: boolean;
}

const StyledFormControlLabel = styled((props: StyledFormControlLabelProps) => (
  <FormControlLabel {...props} />
))(({ theme, checked }) => ({
  // paddingRight: '10px',
  padding: "5px",
  borderRadius: "10px",
  backgroundColor: `${theme.palette.common.white}0D`,
  border: `0.5px solid ${theme.palette.text.secondary}80`,
  color: theme.palette.text.secondary,
  marginLeft: 0,
  marginRight: 0,
  width: "100%",
  columnGap: "5px",
  // marginBottom: '10px',
  '&': checked && {
    backgroundColor: `${theme.palette.primary[500]}1A`,
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: theme.palette.primary[500]
  },
  '.MuiFormControlLabel-label': {
    ...(theme.typography.body_500),
    color: theme.palette.text.secondary,
    '&': checked && {
      color: theme.palette.primary[500],
    },
  },
}));

function MyFormControlLabel(props: FormControlLabelProps) {
  const radioGroup = useRadioGroup();

  let checked = false;

  if (radioGroup) {
    checked = radioGroup.value === props.value;
  }

  return <StyledFormControlLabel checked={checked} {...props} />;
}

export default function UseRadioGroup({
  name,
  formLabel,
  defaultValue,
  items,
  labelColor = 'rgba(38, 48, 60, 0.7)',
  value = '',
  disabled = false,
  onChange,
  onKeyDown,
  sx = {},
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
}: {
  name: string;
  formLabel?: string;
  defaultValue?: string | boolean;
  labelColor?: string;
  items: { label: string; value: string | boolean | number, isDisabled?: boolean }[];
  value?: boolean | string | undefined | number;
  disabled?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  sx?: SxProps<Theme>;
  'aria-labelledby'?: string;
  'aria-label'?: string;
}): JSX.Element {
  const theme = useTheme()
  const [field, meta] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : '';
  const errorId = `${name}-error`;
  const isRequired = useFieldRequired(name);

  const fieldValue = value ? value : field.value;
  onChange = onChange || field.onChange;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = event?.target as HTMLInputElement;

      const syntheticEvent = {
        target: {
          name,
          value: target?.value,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      onChange?.(syntheticEvent);
    }
  };

  const handleCombinedKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    onKeyDown?.(event);

    if (event.defaultPrevented) return;

    handleKeyDown(event as unknown as React.KeyboardEvent<HTMLButtonElement>);
  };

  return (
    <FormControl
      component="fieldset"
      sx={{ width: '100%' }}
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabel}
      aria-required={isRequired || undefined}>
      {formLabel && (
        <FormLabel
          component="legend"
          sx={{ mb: 2, fontWeight: 500, fontSize: 14, color: labelColor }}>
          {formLabel}
        </FormLabel>
      )}
      <RadioGroup
        row
        defaultValue={defaultValue}
        {...field}
        onChange={onChange}
        onKeyDown={handleCombinedKeyDown}
        value={fieldValue}
        aria-labelledby={ariaLabelledBy}
        aria-label={ariaLabel}
        aria-required={isRequired || undefined}
        sx={{
          flexWrap: "nowrap",
          [theme.breakpoints.down('lg')]: {
            flexWrap: "wrap",
            rowGap: "15px"
          },
          columnGap: "15px",
          '& .Mui-disabled': {
            // cursor: !disabled ? 'pointer' : 'not-allowed !important',
            cursor: 'not-allowed !important',
            pointerEvents: 'all',
          },
          '& .Mui-focusVisible': {
            backgroundColor: `${theme.palette.primary[500]}1A`,
          },
          ...sx
        }}
      >
        {items.map((item, index) => (
          <MyFormControlLabel
            key={index}
            label={item.label}
            value={item.value}
            disabled={disabled}
            control={
              <Radio
                disableRipple
                size="small"
                sx={{ ':hover': { background: 'none' } }}
                inputProps={{
                  'aria-describedby': errorText ? errorId : undefined,
                  'aria-invalid': errorText ? true : undefined,
                }}
              />
            }
          />
        ))
        }
      </RadioGroup>
      {
        errorText && (
          <FormHelperText
            id={errorId}
            role="alert"
            error
            sx={{ marginLeft: 'unset', color: `${theme.palette.error[900]}`, fontWeight: 500 }}
          >
            {errorText}
          </FormHelperText>
        )
      }
    </FormControl >
  );
}

const StyledFormControlLabelr = styled((props: StyledFormControlLabelProps) => (
  <FormControlLabel {...props} />
))(({ theme, checked }) => ({
  paddingRight: '10px',
  borderRadius: '12px',
  marginLeft: 0,
  marginBottom: '10px',
  '.MuiFormControlLabel-label': {
    fontWeight: 500,
    fontSize: '16px',
    '&': checked && {
      color: theme.palette.primary.main,
    },
  },
}));

export function MyFormControlLabels(props: FormControlLabelProps): JSX.Element {
  const radioGroup = useRadioGroup();

  let checked = false;

  if (radioGroup) {
    checked = radioGroup.value === props.value;
  }

  return <StyledFormControlLabelr checked={checked} {...props} />;
}

export function UseRadioGroupTransparent({
  name,
  defaultValue,
  items,
  value = '',
  disabled = false,
  formLabel,
  onChange,
  onKeyDown,
  sx,
  TableRender,
  relatedPartyLimit,
  'aria-labelledby': ariaLabelledBy,
  'aria-label': ariaLabel,
}: {
  name: string;
  defaultValue?: string | boolean;
  items: { label: any; value: string | boolean | number }[];
  value?: boolean | string | undefined | number;
  disabled?: boolean;
  formLabel?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  sx?: SxProps;
  TableRender?: any;
  relatedPartyLimit?: string;
  'aria-labelledby'?: string;
  'aria-label'?: string;
}): JSX.Element {
  const theme = useTheme();
  const [field, meta] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : '';
  const errorId = `${name}-error`;
  const isRequired = useFieldRequired(name);

  const fieldValue = value ? value : field.value;
  onChange = onChange || field.onChange;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const target = event?.target as HTMLInputElement;

      const syntheticEvent = {
        target: {
          name,
          value: target?.value,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      onChange?.(syntheticEvent);
    }
  };

  const handleCombinedKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    onKeyDown?.(event);

    if (event.defaultPrevented) return;

    handleKeyDown(event as unknown as React.KeyboardEvent<HTMLButtonElement>);
  };

  return (
    <FormControl
      component="fieldset"
      sx={{ width: '100%', ...sx }}
      aria-labelledby={ariaLabelledBy}
      aria-label={ariaLabel}
      aria-required={isRequired || undefined}>
      {formLabel && (
        <FormLabel component="legend" sx={{ mb: 2, fontWeight: 500, fontSize: 14 }}>
          <Typography sx={{ fontWeight: 500, fontSize: 16, color: `${theme.palette.text.primary}`, fontFamily: `${theme.typography.fontFamily}` }}>
            {formLabel}
          </Typography>
        </FormLabel>
      )}
      <RadioGroup
        row
        defaultValue={defaultValue}
        {...field}
        onChange={onChange}
        onKeyDown={handleCombinedKeyDown}
        value={fieldValue}
        aria-labelledby={ariaLabelledBy}
        aria-label={ariaLabel}
        aria-required={isRequired || undefined}
        sx={{
          '& .Mui-disabled': {
            cursor: !disabled ? 'pointer' : 'not-allowed !important',
            pointerEvents: 'all',
          },
          '& .Mui-focusVisible': {
            backgroundColor: `${theme.palette.primary[500]}1A`,
          }
        }}>
        {items.map((item, index) => (
          <Box key={index} sx={{ display: 'flex', flexDirection: 'column' }}>
            <MyFormControlLabels
              label={item.label}
              value={item.value}
              disabled={disabled}
              control={
                <Radio
                  disableRipple
                  size="small"
                  sx={{ ':hover': { background: 'none' }, color: `${theme.palette.primary[500]}` }}
                  inputProps={{
                    'aria-describedby': errorText ? errorId : undefined,
                    'aria-invalid': errorText ? true : undefined,
                  }}
                />
              }
            />
            {index === 0 && relatedPartyLimit === 'consent' && (
              <Box sx={{ mt: 4, mb: { xs: 4, sm: 0 } }}>{TableRender} </Box>
            )}
          </Box>
        ))}
      </RadioGroup>
      {errorText && (
        <FormHelperText
          id={errorId}
          role="alert"
          error
          sx={{ marginLeft: 'unset', color: `${theme.palette.error[900]}`, fontWeight: 500 }}
        >
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
